import { useState } from 'react'
import { Select } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'

export interface PaginationProps {
  /**
   * Current page number
   */
  current: number
  /**
   * Total number of items
   */
  total: number
  /**
   * Number of items per page
   * @default 50
   */
  pageSize?: number
  /**
   * Callback when page or pageSize changes
   */
  onChange?: (page: number, pageSize: number) => void
  /**
   * Page size options
   * @default ['10', '20', '50', '100']
   */
  pageSizeOptions?: string[]
  /**
   * Show page size changer
   * @default true
   */
  showSizeChanger?: boolean
  /**
   * Show quick jumper
   * @default true
   */
  showQuickJumper?: boolean
  /**
   * Additional className
   */
  className?: string
}

/**
 * Pagination Component
 *
 * A custom pagination component styled according to the AVA design system.
 * Features total count display, page size selector, and quick jump input.
 *
 * @example
 * <Pagination
 *   current={1}
 *   total={100}
 *   pageSize={50}
 *   onChange={(page, pageSize) => handleChange(page, pageSize)}
 * />
 */
export function Pagination({
  current,
  total,
  pageSize = 50,
  onChange,
  pageSizeOptions = ['10', '20', '50', '100'],
  showSizeChanger = true,
  showQuickJumper = true,
  className = '',
}: PaginationProps) {
  const [goToPage, setGoToPage] = useState('')

  const totalPages = Math.ceil(total / pageSize)

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onChange?.(page, pageSize)
    }
  }

  const handlePageSizeChange = (newPageSize: number) => {
    onChange?.(1, newPageSize)
  }

  const handleGoToPage = (value: string) => {
    const pageNum = parseInt(value, 10)
    if (pageNum >= 1 && pageNum <= totalPages) {
      handlePageChange(pageNum)
    }
    setGoToPage('')
  }

  const sizeOptions = pageSizeOptions.map((size) => ({
    value: Number(size),
    label: `${size} / page`,
  }))

  return (
    <div className={`ava-pagination ${className}`}>
      <span className="ava-pagination-total">Total {total} items</span>

      <button
        className="ava-pagination-prev"
        disabled={current <= 1}
        onClick={() => handlePageChange(current - 1)}
      >
        <LeftOutlined />
      </button>

      <span className="ava-pagination-current">{current}</span>

      <button
        className="ava-pagination-next"
        disabled={current >= totalPages}
        onClick={() => handlePageChange(current + 1)}
      >
        <RightOutlined />
      </button>

      {showSizeChanger && (
        <Select
          value={pageSize}
          onChange={handlePageSizeChange}
          className="ava-pagination-size-changer"
          size="small"
          options={sizeOptions}
        />
      )}

      {showQuickJumper && (
        <>
          <span className="ava-pagination-go-to-label">Go to</span>
          <input
            type="text"
            className="ava-pagination-go-to-input"
            value={goToPage}
            onChange={(e) => setGoToPage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleGoToPage((e.target as HTMLInputElement).value)
              }
            }}
            onBlur={(e) => {
              if (e.target.value) {
                handleGoToPage(e.target.value)
              }
            }}
          />
        </>
      )}
    </div>
  )
}
